import { ReactNode, useMemo, useState } from 'react';

// mui
import { Button, Grid, Stack, Typography } from '@mui/material';

// components
import { BottomSheetDialog } from '../BottomSheetDialog/BottomSheetDialog';

interface ConfirmationDialogProps {
  isConfirming?: boolean;
  onCancel: () => void;
  warningText: string;
  confirmText: string;
  confirmationTitle?: string;
  onConfirm: () => void;
}

export const ConfirmationDialog = ({
  isConfirming = true,
  onCancel,
  warningText,
  confirmText,
  confirmationTitle = 'Are you sure?',
  onConfirm,
}: ConfirmationDialogProps) => (
  <BottomSheetDialog
    isOpen={isConfirming}
    onClose={onCancel}
    title={confirmationTitle}
    maxWidth='xs'
    aboveModal
    footer={
      <Stack
        spacing={1.5}
        sx={{
          width: '100%',
        }}
      >
        <Button variant='contained' color='error' onClick={onConfirm} fullWidth>
          {confirmText}
        </Button>
        <Button variant='outlined' onClick={onCancel} fullWidth>
          Cancel
        </Button>
      </Stack>
    }
  >
    <Typography variant='subtitle1'>{warningText}</Typography>
  </BottomSheetDialog>
);

interface DialogActionsProps {
  variant?: any;
  size?: any;
  color?: any;
  isDisabled?: boolean;
  label: string;
  onAction: () => void;
  isConfirmable?: boolean;
  confirmText?: string;
  warningText?: string;
}

const DialogAction = ({
  variant,
  size,
  color,
  isDisabled,
  label,
  onAction,
}: DialogActionsProps) => {
  return (
    <Button
      variant={variant}
      size={size}
      color={color}
      onClick={onAction}
      fullWidth
      disabled={isDisabled}
    >
      {label}
    </Button>
  );
};

interface DialogWithConfirmActionsProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  actions: DialogActionsProps[];
  /**
   * Footer layout: `grid` (default) lays the actions out as right-aligned
   * columns; `stack` is full-width buttons stacked vertically (better for a
   * primary CTA + secondary actions, e.g. the member action dialogs).
   */
  actionsLayout?: 'grid' | 'stack';
  children: ReactNode;
}

export const DialogWithConfirmActions = ({
  title,
  isOpen,
  onClose,
  actions,
  actionsLayout = 'grid',
  children,
}: DialogWithConfirmActionsProps) => {
  const [confirmation, setConfirmation] =
    useState<ConfirmationDialogProps | null>(null);

  const newActions = useMemo(
    () =>
      actions.map((action) => ({
        ...action,
        onAction: action.isConfirmable
          ? () =>
              setConfirmation({
                isConfirming: true,
                onCancel: () => setConfirmation(null),
                warningText:
                  action.warningText || 'This action cannot be undone',
                confirmText: action.confirmText || 'Perform action',
                onConfirm: () => {
                  action.onAction();
                  setConfirmation(null);
                },
              })
          : action.onAction,
      })),
    [actions],
  );

  return (
    <>
      <BottomSheetDialog
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        footer={
          actionsLayout === 'stack' ? (
            <Stack
              spacing={1.5}
              sx={{
                width: '100%',
              }}
            >
              {newActions.map((action, index) => (
                <DialogAction key={index} {...action} />
              ))}
            </Stack>
          ) : (
            <Grid
              container
              spacing={1.5}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {newActions.map((action, index) => (
                <Grid key={index} size={{ xs: 12, sm: 3 }}>
                  <DialogAction {...action} />
                </Grid>
              ))}
            </Grid>
          )
        }
      >
        {children}
      </BottomSheetDialog>
      {confirmation && <ConfirmationDialog {...confirmation} />}
    </>
  );
};

DialogWithConfirmActions.displayName = 'DialogWithConfirmActions';
export default DialogWithConfirmActions;
